import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: component in src/pages/insurance/claim-detail/ requires four levels up to reach convex/_generated/
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileText, Download } from 'lucide-react';
import { formatFileSize, formatDate } from './claimDetailUtils';
import type { TDocument } from './claimDetailTypes';

function ClaimDocumentRow({ doc }: { doc: TDocument }) {
  const fileUrl = useQuery(api.storage.getUrl, { storageId: doc.storageId });
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const hasAnalysis = !!doc.aiAnalysis && Object.keys(doc.aiAnalysis).length > 0;

  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{doc.fileName}</p>
          <p className="text-xs text-muted-foreground">
            {doc.documentType} · {formatFileSize(doc.fileSize)} · {formatDate(doc.createdAt)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {hasAnalysis && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAnalysisOpen(true)}>
            AI Analysis
          </Button>
        )}
        {fileUrl && (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
            <a href={fileUrl} download={doc.fileName} target="_blank" rel="noreferrer">
              <Download className="h-3.5 w-3.5" />
              <span className="sr-only">Download {doc.fileName}</span>
            </a>
          </Button>
        )}
      </div>
      {hasAnalysis && (
        <Dialog open={analysisOpen} onOpenChange={setAnalysisOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>AI Analysis — {doc.fileName}</DialogTitle>
            </DialogHeader>
            <pre className="text-xs bg-muted rounded p-3 overflow-auto max-h-96 whitespace-pre-wrap">
              {JSON.stringify(doc.aiAnalysis, null, 2)}
            </pre>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface IClaimDocumentsPanelProps {
  documents: TDocument[];
}

export function ClaimDocumentsPanel({ documents }: IClaimDocumentsPanelProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Documents
          <span className="text-xs font-normal text-muted-foreground">
            ({documents.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents uploaded.</p>
        ) : (
          documents.map(doc => <ClaimDocumentRow key={doc._id} doc={doc} />)
        )}
      </CardContent>
    </Card>
  );
}
